import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SaveCompany from '../components/prospector/prospecting/SaveCompany';
import EmailModal from '../components/leads/EmailModal';


// PeopleTab component for company team members
const PeopleTab = ({ company, websiteData }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [teamSuccess, setTeamSuccess] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(false);
  const [addingLead, setAddingLead] = useState(null); // Track which team member is being added as a lead
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [extractionAbortController, setExtractionAbortController] = useState(null);
  const [loadingSnovProspects, setLoadingSnovProspects] = useState(false);
  const [snovProspectsAbortController, setSnovProspectsAbortController] = useState(null);
  const [loadingEmails, setLoadingEmails] = useState({}); // Track email loading by prospect name
  const [snovEnabled, setSnovEnabled] = useState(false); // Track SNOV feature flag
  const [checkingSnovAccess, setCheckingSnovAccess] = useState(true);

  // Function to add a team member as a lead in the CRM system
  const addAsLead = async (member, company) => {
    // Set the loading state for this specific member
    setAddingLead(member.id || member.name);

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required. Please log in.');
        setAddingLead(null);
        return;
      }

      // Add token to request headers
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Split full name into first name and last name
      let firstName = '';
      let lastName = '';

      if (member.name) {
        const nameParts = member.name.trim().split(/\s+/);
        if (nameParts.length === 1) {
          firstName = nameParts[0];
        } else if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        }
      }

      // Determine country from company address or domain
      let country = '';
      if (company.forretningsadresse && company.forretningsadresse.land) {
        country = company.forretningsadresse.land;
      } else if (company.forretningsadresse && company.forretningsadresse.landkode) {
        // Convert country code to full name if available
        const countryMap = {
          'NO': 'Norway',
          'SE': 'Sweden',
          'DK': 'Denmark',
          'FI': 'Finland'
        };
        country = countryMap[company.forretningsadresse.landkode] || company.forretningsadresse.landkode;
      } else {
        // Default to Norway for .no domains or if no country info is available
        country = 'Norway';
      }

      // Create the lead data - map team member fields to lead fields
      const leadData = {
        name: member.name,
        first_name: firstName,
        last_name: lastName,
        position: member.position || '',
        role: member.position || '',
        company: company.navn,
        email: member.email || '',
        phone: member.phone || '',
        country: country,
        original_source: `Prospecting: ${company.navn} (${company.organisasjonsnummer})`,
        latest_source: `Prospecting: ${company.navn} (${company.organisasjonsnummer})`
      };

      // Make API call to create a new lead
      const response = await axios.post('/api/leads', leadData, config);

      if (response.data && response.data.id) {
        // Show success message
        alert(`Successfully added ${member.name} as a lead`);
      }
    } catch (err) {
      console.error('Error adding team member as lead:', err);
      
      // Handle specific error types
      if (err.response?.data?.error_type === 'duplicate_email') {
        alert(`⚠️ ${member.name} is already in your CRM database with email: ${member.email}`);
      } else if (err.response?.data?.message) {
        alert(`❌ Failed to add lead: ${err.response.data.message}`);
      } else {
        alert(`❌ Failed to add lead: ${err.message}`);
      }
    } finally {
      setAddingLead(null);
    }
  };


  // Function to open email modal for team member - prepare data but don't create lead yet
  const handleEmailTeamMember = (member, company) => {
    // Split full name into first name and last name
    let firstName = '';
    let lastName = '';

    if (member.name) {
      const nameParts = member.name.trim().split(/\s+/);
      if (nameParts.length === 1) {
        firstName = nameParts[0];
      } else if (nameParts.length >= 2) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' ');
      }
    }

    // Create a lead-like object for the EmailModal but without an ID
    const leadData = {
      id: null, // No ID yet - will be created when email is sent
      email: member.email,
      name: member.name,
      first_name: firstName,
      last_name: lastName,
      company: company.navn,
      position: member.position || '',
      // Store additional data needed for lead creation
      _prospectorData: {
        member,
        company
      }
    };
    
    setEmailRecipient(leadData);
    setShowEmailModal(true);
  };

  // Function to extract team members
  const extractTeamMembers = async () => {
    if (!websiteData || websiteData.length === 0) {
      setTeamError("No website available to extract team members from");
      return;
    }

    // Cancel any existing extraction
    if (extractionAbortController) {
      extractionAbortController.abort();
    }

    // Create new AbortController for this extraction
    const abortController = new AbortController();
    setExtractionAbortController(abortController);

    setLoadingTeam(true);
    setTeamError(null);

    // Show longer extraction message after 3 seconds
    const loadingMessageTimer = setTimeout(() => {
      if (loadingTeam) {
        setTeamError("The extraction process is taking some time. We're scanning the company website pages to find team members...");
      }
    }, 3000);

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setTeamError('Authentication required. Please log in.');
        setLoadingTeam(false);
        return;
      }

      // Add token to request headers and abort signal
      const config = {
        headers: { 'x-auth-token': token },
        signal: abortController.signal
      };

      // API call to extract team members
      const response = await axios.post(
        `/api/prospector/companies/${company.organisasjonsnummer}/extract-team`,
        {
          domain: websiteData[0].domain,
          companyName: company.navn
        },
        config
      );

      if (response.data && response.data.teamMembers && response.data.teamMembers.length > 0) {
        setTeamMembers(response.data.teamMembers);
        setTeamError(null); // Clear any previous errors
      } else if (response.data && Array.isArray(response.data)) {
        // Direct array in response (old API format)
        setTeamMembers(response.data);
        setTeamError(null);
      } else {
        setTeamMembers([]);
        setTeamError('No team members found on the company website. The system searched common pages like "Om oss", "Ansatte", and "Kontakt".');
      }
    } catch (err) {
      if (err.name === 'CanceledError') {
        console.log('Team extraction request canceled');
        return;
      }
      console.error('Error extracting team members:', err);
      setTeamError('Failed to extract team members. This could be due to website structure or connection issues.');
      setTeamMembers([]);
    } finally {
      clearTimeout(loadingMessageTimer);
      if (!abortController.signal.aborted) {
        setLoadingTeam(false);
      }
      setExtractionAbortController(null);
    }
  };

  // Function to extract prospects from leadbooster
  const extractSnovProspects = async () => {
    // Cancel any existing extraction
    if (snovProspectsAbortController) {
      snovProspectsAbortController.abort();
    }

    // Create new AbortController for this extraction
    const abortController = new AbortController();
    setSnovProspectsAbortController(abortController);

    setLoadingSnovProspects(true);
    setTeamError(null);

    // Show longer extraction message after 5 seconds
    const loadingMessageTimer = setTimeout(() => {
      if (loadingSnovProspects) {
        setTeamError("Searching leadbooster database for company prospects. This may take up to 20 seconds...");
      }
    }, 5000);

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setTeamError('Authentication required. Please log in.');
        setLoadingSnovProspects(false);
        return;
      }

      // Add token to request headers and abort signal
      const config = {
        headers: { 'x-auth-token': token },
        signal: abortController.signal
      };

      // Prepare request data
      const requestData = {
        companyName: company.navn
      };

      // Add domain if available
      if (websiteData && websiteData.length > 0) {
        requestData.domain = websiteData[0].domain;
      }

      // API call to extract prospects from leadbooster
      const response = await axios.post(
        `/api/prospector/companies/${company.organisasjonsnummer}/snov-prospects`,
        requestData,
        config
      );

      if (response.data && response.data.prospects && response.data.prospects.length > 0) {
        // Merge with existing team members or replace them
        const existingWebsiteMembers = teamMembers.filter(member => member.source !== 'snov.io');
        const allMembers = [...existingWebsiteMembers, ...response.data.prospects];
        
        setTeamMembers(allMembers);
        setTeamError(null);

        // Show success message
        const decisionMakersCount = response.data.decisionMakers || 0;
        const successMessage = `✅ Found ${response.data.total} prospects from leadbooster${decisionMakersCount > 0 ? ` (${decisionMakersCount} decision makers)` : ''}`;
        setTeamSuccess(successMessage);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setTeamSuccess(null);
        }, 5000);

      } else {
        setTeamError('No prospects found in leadbooster database for this company. The database might not have coverage for this specific company.');
      }
    } catch (err) {
      if (err.name === 'CanceledError') {
        console.log('leadbooster prospects request canceled');
        return;
      }
      console.error('Error extracting leadbooster prospects:', err);
      if (err.response?.data?.message) {
        setTeamError(`leadbooster search failed: ${err.response.data.message}`);
      } else {
        setTeamError('Failed to search leadbooster database. This could be due to API limits or connection issues.');
      }
    } finally {
      clearTimeout(loadingMessageTimer);
      if (!abortController.signal.aborted) {
        setLoadingSnovProspects(false);
      }
      setSnovProspectsAbortController(null);
    }
  };

  // Function to get email for a specific leadbooster prospect
  const getProspectEmail = async (member) => {
    if (member.source !== 'snov.io' || member.email) {
      return;
    }

    // Extract first name and last name from the member name
    const nameParts = member.name.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    if (!firstName || !lastName) {
      alert('Cannot extract first and last name from prospect name');
      return;
    }

    // Extract domain from company website
    let domain = '';
    if (company.hjemmeside) {
      try {
        const url = company.hjemmeside.includes('://') ? company.hjemmeside : `https://${company.hjemmeside}`;
        const urlObj = new URL(url);
        domain = urlObj.hostname.replace('www.', '');
      } catch (error) {
        console.error('Error parsing company website URL:', error);
        alert('Cannot extract domain from company website');
        return;
      }
    } else {
      alert('Company website not available for email search');
      return;
    }

    const memberKey = member.name || `prospect_${Date.now()}`;
    
    // Set loading state for this specific prospect
    setLoadingEmails(prev => ({
      ...prev,
      [memberKey]: true
    }));

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required. Please log in.');
        return;
      }

      // Add token to request headers
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // API call to get prospect email using Email Finder API
      const response = await axios.post(
        `/api/prospector/companies/${company.organisasjonsnummer}/snov-email`,
        {
          firstName,
          lastName,
          domain,
          prospectName: member.name
        },
        config
      );

      if (response.data && response.data.success && response.data.email) {
        const foundEmail = response.data.email;
        
        // Save email to database
        try {
          await axios.put(
            `/api/prospector/companies/${company.organisasjonsnummer}/team-members/${member.id}/email`,
            {
              email: foundEmail,
              source: 'snov.io'
            },
            config
          );
          console.log(`Saved email to database for ${member.name}`);
        } catch (saveError) {
          console.error('Error saving email to database:', saveError);
          // Don't show error to user since email discovery still worked
        }
        
        // Update the team member with the found email
        setTeamMembers(prevMembers => 
          prevMembers.map(m => 
            m.name === member.name && m.source === 'snov.io'
              ? { ...m, email: foundEmail }
              : m
          )
        );

        // Show success message briefly
        setTeamSuccess(`✅ Found email for ${member.name}: ${foundEmail}`);
        setTimeout(() => {
          setTeamSuccess(null);
        }, 5000);
      } else {
        // Show no email found message
        setTeamError(`❌ No email found for ${member.name} in leadbooster database`);
        setTimeout(() => {
          setTeamError(null);
        }, 5000);
      }
    } catch (err) {
      console.error('Error getting prospect email:', err);
      if (err.response?.data?.message) {
        setTeamError(`Failed to get email for ${member.name}: ${err.response.data.message}`);
      } else {
        setTeamError(`Failed to get email for ${member.name}. This could be due to API limits.`);
      }
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setTeamError(null);
      }, 5000);
    } finally {
      // Clear loading state for this prospect
      setLoadingEmails(prev => {
        const newState = { ...prev };
        delete newState[memberKey];
        return newState;
      });
    }
  };

  // Function to check SNOV access for current company
  const checkSnovAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('/api/settings/company', {
        headers: { 'x-auth-token': token }
      });
      
      // Check if the company has SNOV enabled
      setSnovEnabled(response.data.snov_enabled || response.data.company?.snov_enabled || false);
    } catch (error) {
      console.error('Error checking SNOV access:', error);
      setSnovEnabled(false);
    } finally {
      setCheckingSnovAccess(false);
    }
  };

  // Function to load saved team members
  const loadSavedTeamMembers = async () => {
    setLoadingTeam(true);
    setTeamError(null);

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setTeamError('Authentication required. Please log in.');
        setLoadingTeam(false);
        return;
      }

      // Add token to request headers
      const config = {
        headers: { 'x-auth-token': token }
      };

      // API call to get saved team members
      const response = await axios.get(
        `/api/prospector/companies/${company.organisasjonsnummer}/team-members`,
        config
      );

      // Check for the nested team_members array in the response
      if (response.data && response.data.team_members && response.data.team_members.length > 0) {
        setTeamMembers(response.data.team_members);
      } else {
        setTeamMembers([]);
        // Don't set error here - it's fine to have no saved members
      }
    } catch (err) {
      console.error('Error loading team members:', err);
      setTeamError('Failed to load team members');
    } finally {
      setLoadingTeam(false);
    }
  };

  // Function to delete all team members
  const deleteAllTeamMembers = async () => {
    if (!company?.organisasjonsnummer) {
      return;
    }

    if (!window.confirm("Are you sure you want to delete all team members? This is intended for testing purposes only.")) {
      return;
    }

    setDeletingTeam(true);
    setTeamError(null);

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setTeamError('Authentication required. Please log in.');
        setDeletingTeam(false);
        return;
      }

      // Add token to request headers
      const config = {
        headers: { 'x-auth-token': token }
      };

      // API call to delete all team members
      await axios.delete(
        `/api/prospector/companies/${company.organisasjonsnummer}/team-members`,
        config
      );

      // Clear the team members from state
      setTeamMembers([]);

    } catch (err) {
      console.error('Error deleting team members:', err);
      setTeamError('Failed to delete team members: ' + (err.response?.data?.msg || err.message));
    } finally {
      setDeletingTeam(false);
    }
  };

  // Load saved team members and check SNOV access on first render or when company changes
  useEffect(() => {
    if (company?.organisasjonsnummer) {
      loadSavedTeamMembers();
    }
    // Check SNOV access for current company
    checkSnovAccess();
  }, [company?.organisasjonsnummer]);

  // Cleanup function to cancel team extraction on unmount
  useEffect(() => {
    return () => {
      if (extractionAbortController) {
        extractionAbortController.abort();
      }
      if (snovProspectsAbortController) {
        snovProspectsAbortController.abort();
      }
    };
  }, [extractionAbortController, snovProspectsAbortController]);

  return (
    <div className="p-6">
      {/* Header with Extract button */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Team Members</h2>

        <div className="flex flex-wrap gap-3">
          {teamMembers.length > 0 && (
            <button
              onClick={deleteAllTeamMembers}
              disabled={deletingTeam || loadingTeam || loadingSnovProspects}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none disabled:opacity-50"
            >
              {deletingTeam ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete All (Testing)
                </>
              )}
            </button>
          )}

          {websiteData && websiteData.length > 0 && (
            <button
              onClick={extractTeamMembers}
              disabled={loadingTeam || deletingTeam || loadingSnovProspects}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50"
              title="Extract team members from company website"
            >
              {loadingTeam ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Extracting...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Extract from Website
                </>
              )}
            </button>
          )}

          {snovEnabled && (
            <button
              onClick={extractSnovProspects}
              disabled={loadingTeam || deletingTeam || loadingSnovProspects}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none disabled:opacity-50"
              title="Find prospects using leadbooster database"
            >
            {loadingSnovProspects ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Find with leadbooster
              </>
            )}
            </button>
          )}
        </div>
      </div>

      {/* Team members list */}
      {teamSuccess && (
        <div className="bg-green-50 border-green-400 border-l-4 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{teamSuccess}</p>
            </div>
          </div>
        </div>
      )}
      {teamError && (
        <div className={`${loadingTeam ? "bg-blue-50 border-blue-400" : "bg-red-50 border-red-400"} border-l-4 p-4 mb-4`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {loadingTeam ? (
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm ${loadingTeam ? "text-blue-700" : "text-red-700"}`}>{teamError}</p>

              {/* Additional information for certain error types */}
              {!loadingTeam && teamError.includes('No team members found') && (
                <div className="mt-2 text-xs text-gray-600">
                  <p>Some reasons team members may not be found:</p>
                  <ul className="list-disc ml-4 mt-1">
                    <li>The company website doesn't have a dedicated team/about page</li>
                    <li>Team information is displayed in an uncommon format</li>
                    <li>The website may use JavaScript to load team data</li>
                    <li>The company might list team members on social media instead</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(loadingTeam || loadingSnovProspects) && teamMembers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mb-4 ${loadingSnovProspects ? 'border-purple-500' : 'border-blue-500'}`}></div>
          <p className="text-gray-600">
            {loadingSnovProspects ? 'Searching...' : 'Scanning company website for team members...'}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {loadingSnovProspects ? 'This process may take up to 20 seconds' : 'This process may take up to 30 seconds'}
          </p>
        </div>
      ) : teamMembers.length > 0 ? (
        <div>
          <div className="mb-4 pb-1 border-b border-gray-200">
            <h3 className="text-md font-semibold text-gray-700 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Team Members
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Sort team members so key decision makers come first */}
            {[...teamMembers]
            .sort((a, b) => {
              // Calculate if they are key decision makers
              const aIsKey = a.is_key_decision_maker ||
                (a.position && /leder|ceo|coo|cfo|cmo|manager|director|eier|innehaver/i.test(a.position));
              const bIsKey = b.is_key_decision_maker ||
                (b.position && /leder|ceo|coo|cfo|cmo|manager|director|eier|innehaver/i.test(b.position));

              // Key decision makers first
              if (aIsKey && !bIsKey) return -1;
              if (!aIsKey && bIsKey) return 1;

              // For members of same key decision maker status, check completeness score
              const aHasTitle = !!a.position;
              const aHasPhone = !!a.phone;
              const aHasEmail = !!a.email;
              const aCompletenessScore = (aHasTitle ? 1 : 0) + (aHasPhone ? 1 : 0) + (aHasEmail ? 1 : 0);

              const bHasTitle = !!b.position;
              const bHasPhone = !!b.phone;
              const bHasEmail = !!b.email;
              const bCompletenessScore = (bHasTitle ? 1 : 0) + (bHasPhone ? 1 : 0) + (bHasEmail ? 1 : 0);

              // Sort by completeness score (higher score first)
              if (aCompletenessScore !== bCompletenessScore) {
                return bCompletenessScore - aCompletenessScore;
              }

              // Then alphabetically by name
              return (a.name || '').localeCompare(b.name || '');
            })
            .map((member, index) => (
            <div key={index} className={`${
              // Check if this person is a key decision maker either by flag or position title
              (member.is_key_decision_maker ||
               (member.position && /leder|ceo|coo|cfo|cmo|manager|director|eier|innehaver/i.test(member.position)))
              ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'
            } rounded-lg shadow-md overflow-hidden border relative`}>
              <div className="p-6 relative">
                {/* Source badge */}
                <div className="absolute top-2 left-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    member.source === 'snov.io' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {member.source === 'snov.io' ? (
                      <>
                        <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        leadbooster
                      </>
                    ) : (
                      <>
                        <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                        </svg>
                        Website
                      </>
                    )}
                  </span>
                </div>

                {/* Add as Lead button in top right */}
                <button
                  onClick={() => addAsLead(member, company)}
                  disabled={addingLead === (member.id || member.name)}
                  className="absolute top-2 right-2 px-2 py-1 bg-white hover:bg-blue-50 text-blue-600 text-xs rounded-md border border-blue-300 shadow-sm flex items-center disabled:bg-gray-100 disabled:text-gray-400"
                  title="Add to CRM as lead"
                >
                  {addingLead === (member.id || member.name) ? (
                    <svg className="animate-spin h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <>+ Lead</>
                  )}
                </button>
                <div className="flex items-start">
                  {member.image_url || member.imageUrl ? (
                    <div className="flex-shrink-0 h-16 w-16 rounded-full overflow-hidden bg-gray-100 mr-4">
                      <img
                        src={member.image_url || member.imageUrl}
                        alt={member.name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/80?text=' + encodeURIComponent(member.name?.charAt(0) || '?');
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 h-16 w-16 rounded-full overflow-hidden bg-blue-100 flex items-center justify-center mr-4">
                      <span className="text-2xl font-semibold text-blue-700">
                        {member.name?.charAt(0) || '?'}
                      </span>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center">
                      <h3 className="text-lg font-medium text-gray-900">{member.name}</h3>
                      {/* Show star icon for key decision makers */}
                      {(member.is_key_decision_maker ||
                        (member.position && /leder|ceo|coo|cfo|cmo|manager|director|eier|innehaver/i.test(member.position))) && (
                        <svg xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 ml-1 text-yellow-500"
                          viewBox="0 0 20 20"
                          fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      )}
                    </div>
                    {member.position && (
                      <p className={`mt-1 text-sm ${
                        (member.is_key_decision_maker ||
                         (member.position && /leder|ceo|coo|cfo|cmo|manager|director|eier|innehaver/i.test(member.position)))
                        ? 'text-green-700 font-medium' : 'text-gray-600'
                      }`}>
                        {member.position}
                      </p>
                    )}

                    <div className="mt-3 space-y-1">
                      {member.email && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEmailTeamMember(member, company);
                          }}
                          className="text-sm text-blue-600 hover:underline flex items-center cursor-pointer"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {member.email}
                        </button>
                      )}
                      {member.source === 'snov.io' && !member.email && snovEnabled && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            getProspectEmail(member);
                          }}
                          disabled={loadingEmails[member.name]}
                          className="text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white px-3 py-1 rounded flex items-center"
                        >
                          {loadingEmails[member.name] ? (
                            <>
                              <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Getting...
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Get Email
                            </>
                          )}
                        </button>
                      )}
                      {member.phone && (
                        <a
                          href={`tel:${member.phone.replace(/\s+/g, '')}`}
                          className="text-sm text-blue-600 hover:underline flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {member.phone}
                        </a>
                      )}
                    </div>

                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <h3 className="text-xl font-medium text-gray-800 mb-2">No Team Members Found</h3>

          {websiteData && websiteData.length > 0 ? (
            <div>
              <p className="text-gray-600 max-w-md mx-auto mb-4">
                Click the 'Extract Team Members' button to search for team members from the company website.
              </p>
            </div>
          ) : (
            <p className="text-gray-600 max-w-md mx-auto">
              No website is available to extract team members from.
            </p>
          )}

        </div>
      )}

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={(emailSent) => {
          setShowEmailModal(false);
          // Show success notification that lead was created/found and email sent
          if (emailSent && emailRecipient?.name) {
            alert(`Email sent successfully to ${emailRecipient.name}!`);
          }
          setEmailRecipient(null);
        }}
        lead={emailRecipient}
      />
    </div>
  );
};

// ProspectorDetail page - This is a full page view of company details
const ProspectorDetail = () => {
  const { orgNumber } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const [financialData, setFinancialData] = useState([]);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialError, setFinancialError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [websiteData, setWebsiteData] = useState(null);
  const [websiteLoading, setWebsiteLoading] = useState(false);
  const [websiteError, setWebsiteError] = useState(null);

  // Check for referrer to determine back button
  const [fromListView, setFromListView] = useState(false);

  // Track current route for restoration
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/prospector')) {
      localStorage.setItem('prospector_last_route', currentPath);
    }
  }, [orgNumber]);

  useEffect(() => {
    // First check local storage since it's more reliable
    const fromList = localStorage.getItem('prospector_from_list');
    if (fromList) {
      console.log('Found prospector_from_list in localStorage:', fromList);
      setFromListView(true);
      // Force the flag to true to ensure it's set properly
      localStorage.setItem('prospector_from_list', 'true');
    } else {
      // Fallback to check referrer if localStorage flag isn't set
      const referrer = document.referrer;
      if (referrer && referrer.includes('/prospector/lists/')) {
        console.log('Referrer indicates coming from list view:', referrer);
        setFromListView(true);
        // Set the flag in case we need it later (like after a page refresh)
        localStorage.setItem('prospector_from_list', 'true');
      }
    }
  }, []);

  // Add debugging effect to monitor state changes
  useEffect(() => {
    console.log('fromListView state updated:', fromListView);
  }, [fromListView]);

  // Add click away listener to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      const dropdownElements = document.querySelectorAll('.save-dropdown');
      const plusButtonElements = document.querySelectorAll('.save-dropdown-trigger');

      let clickedInsideDropdown = false;
      dropdownElements.forEach(element => {
        if (element.contains(event.target)) {
          clickedInsideDropdown = true;
        }
      });

      let clickedOnTrigger = false;
      plusButtonElements.forEach(element => {
        if (element.contains(event.target)) {
          clickedOnTrigger = true;
        }
      });

      if (showSaveDropdown && !clickedInsideDropdown && !clickedOnTrigger) {
        setShowSaveDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSaveDropdown]);

  // Fetch company details
  useEffect(() => {
    if (!orgNumber) {
      setError('Organization number is missing');
      setLoading(false);
      return;
    }

    const fetchCompanyDetails = async () => {
      setLoading(true);
      
      try {
        // Get auth token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No authentication token available');
          setError('Authentication required. Please log in.');
          setLoading(false);
          return;
        }

        // Add token to request headers
        const config = {
          headers: { 'x-auth-token': token }
        };

        const response = await axios.get(`/api/prospector/companies/${orgNumber}`, config);
        setCompany(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching company details:', err);
        if (err.response && err.response.status === 404) {
          setError(`No company found with organization number ${orgNumber}`);
        } else {
          setError('An error occurred while fetching company information');
        }
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, [orgNumber]);

  // Fetch financial data when company changes
  useEffect(() => {
    if (company?.organisasjonsnummer) {
      setFinancialLoading(true);
      
      const fetchFinancialData = async () => {
        try {
          // Get auth token from localStorage
          const token = localStorage.getItem('token');
          if (!token) {
            console.error('No authentication token available');
            setFinancialError('Authentication required. Please log in.');
            setFinancialLoading(false);
            return;
          }

          // Add token to request headers
          const config = {
            headers: { 'x-auth-token': token }
          };

          const response = await axios.get(`/api/prospector/companies/${orgNumber}/financials`, config);
          
          // Handle both single object and array responses
          let dataArray = Array.isArray(response.data) ? response.data : [response.data];
          
          // Sort by year (most recent first)
          const sortedData = dataArray.sort((a, b) => {
            const yearA = new Date(a.regnskapsperiode?.fraDato).getFullYear();
            const yearB = new Date(b.regnskapsperiode?.fraDato).getFullYear();
            return yearB - yearA;
          });
          
          setFinancialData(sortedData);
          setFinancialError(null);
        } catch (err) {
          console.error('Error fetching financial data:', err);
          setFinancialError('Could not retrieve financial data');
        } finally {
          setFinancialLoading(false);
        }
      };
      
      fetchFinancialData();
    }
  }, [company?.organisasjonsnummer, orgNumber]);

  // Fetch website data and screenshot
  useEffect(() => {
    if (company?.navn && company?.organisasjonsnummer) {
      setWebsiteLoading(true);
      
      // Create AbortController to cancel request if component unmounts
      const abortController = new AbortController();
      
      const fetchWebsiteData = async () => {
        try {
          // Get auth token from localStorage
          const token = localStorage.getItem('token');
          if (!token) {
            console.error('No authentication token available');
            setWebsiteError('Authentication required. Please log in.');
            setWebsiteLoading(false);
            return;
          }

          // Add token to request headers and abort signal
          const config = {
            headers: { 'x-auth-token': token },
            signal: abortController.signal
          };

          // Discover websites - only try one API for now to simplify
          const response = await axios.get(
            `/api/prospector/companies/${company.organisasjonsnummer}/website?companyName=${encodeURIComponent(company.navn)}`, 
            config
          );
          
          if (response.data.websites && response.data.websites.length > 0) {
            setWebsiteData(response.data.websites);
            setWebsiteError(null);
          } else {
            setWebsiteData([]);
            setWebsiteError('No website found for this company');
          }
        } catch (err) {
          if (err.name === 'CanceledError') {
            console.log('Website discovery request canceled');
            return;
          }
          console.error('Error fetching website data:', err);
          setWebsiteError('Could not retrieve website information');
          setWebsiteData([]);
        } finally {
          if (!abortController.signal.aborted) {
            setWebsiteLoading(false);
          }
        }
      };
      
      fetchWebsiteData();
      
      // Cleanup function to cancel request on unmount or company change
      return () => {
        abortController.abort();
      };
    }
  }, [company?.navn, company?.organisasjonsnummer]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // For the tabs
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'info', label: 'Company Info' },
    { id: 'people', label: 'People' },
    { id: 'financials', label: 'Financials' }
  ];

  // Handle back button click - Updated to support list IDs and search persistence
  const handleBackClick = () => {
    // If we came from a list view, go to the lists page
    if (fromListView) {
      // Get the list ID
      const listId = localStorage.getItem('prospector_list_id');

      // Clear the flags when navigating away
      localStorage.removeItem('prospector_from_list');
      localStorage.removeItem('prospector_list_id');

      // Navigate to the specific list if available, otherwise to the main lists page
      if (listId) {
        navigate(`/prospector/lists/${listId}`);
      } else {
        navigate('/prospector/lists');
      }
    } else {
      // For search page, keep the search results in localStorage
      // so they're available when returning to the search page
      // Set flag to prevent auto-redirect when returning to search
      localStorage.setItem('prospector_dont_redirect', 'true');
      navigate('/prospector');
    }
  };

  // Add a cleanup function to clear saved search when navigating away from the prospector module
  useEffect(() => {
    return () => {
      // This will run when the component unmounts, but we only want to clear if navigating outside /prospector
      const handleBeforeUnload = () => {
        const currentPath = window.location.pathname;
        if (!currentPath.startsWith('/prospector')) {
          localStorage.removeItem('prospector_search_results');
          localStorage.removeItem('prospector_search_params');
          localStorage.removeItem('prospector_pagination');
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // We're removing the convert to lead function as we now use lists instead

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6 flex items-center">
          <button
            onClick={handleBackClick}
            className={`mr-4 inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md ${fromListView ? 'text-green-700 bg-green-100 hover:bg-green-200' : 'text-blue-700 bg-blue-100 hover:bg-blue-200'}`}
          >
            &larr; Back to {fromListView ? 'list' : 'search'}
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            Company Details
          </h1>
        </div>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4">Loading company information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6 flex items-center">
          <button
            onClick={handleBackClick}
            className={`mr-4 inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md ${fromListView ? 'text-green-700 bg-green-100 hover:bg-green-200' : 'text-blue-700 bg-blue-100 hover:bg-blue-200'}`}
          >
            &larr; Back to {fromListView ? 'list' : 'search'}
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            Company Details
          </h1>
        </div>
        <div className="p-8 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6 flex items-center">
          <button
            onClick={handleBackClick}
            className={`mr-4 inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md ${fromListView ? 'text-green-700 bg-green-100 hover:bg-green-200' : 'text-blue-700 bg-blue-100 hover:bg-blue-200'}`}
          >
            &larr; Back to {fromListView ? 'list' : 'search'}
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            Company Details
          </h1>
        </div>
        <div className="p-8 text-center">
          <p>No company selected</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6 flex items-center">
        <button
          onClick={handleBackClick}
          className={`mr-4 inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md ${fromListView ? 'text-green-700 bg-green-100 hover:bg-green-200' : 'text-blue-700 bg-blue-100 hover:bg-blue-200'}`}
        >
          &larr; Back to {fromListView ? 'list' : 'search'}
        </button>
        <h1 className="text-3xl font-bold text-gray-800">
          Company Details
        </h1>
      </div>
      
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Company header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{company.navn}</h1>
              <p className="text-gray-600">Organization number: {company.organisasjonsnummer}</p>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowSaveDropdown(!showSaveDropdown)}
                className="p-2 bg-blue-50 rounded-full text-blue-600 hover:bg-blue-100 focus:outline-none save-dropdown-trigger"
                title="Save to list"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>

              {showSaveDropdown && (
                <div
                  className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 overflow-hidden save-dropdown"
                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                >
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Save to List</h3>
                  </div>
                  <div className="p-4">
                    {company && <SaveCompany
                      company={company}
                      onSuccess={() => {
                        // Close the dropdown after a successful save
                        setTimeout(() => setShowSaveDropdown(false), 1500);
                      }}
                      isDropdown={true}
                    />}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Tab navigation */}
        <div className="border-b">
          <div className="px-6">
            <nav className="-mb-px flex space-x-8">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 text-sm font-medium ${
                    activeTab === tab.id 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
        
        {/* Tab content */}
        <div>
          {activeTab === 'overview' && (
            <div className="p-6">
              <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Website */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 h-full">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <h2 className="text-lg font-semibold text-gray-800">Website</h2>
                    </div>
                    
                    {websiteLoading ? (
                      <div className="p-6 flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    ) : websiteData && websiteData.length > 0 ? (
                      <div className="p-6 flex flex-col items-center justify-center text-center h-full">
                        <div className="mb-4 w-full">
                          {websiteData[0].screenshotUrl && (
                            <div className="mb-4 overflow-hidden rounded-md border border-gray-200">
                              <img
                                src={
                                  websiteData[0].screenshotUrl.startsWith('http')
                                    ? websiteData[0].screenshotUrl
                                    : websiteData[0].screenshotUrl.startsWith('/uploads')
                                      ? websiteData[0].screenshotUrl
                                      : `/uploads${websiteData[0].screenshotUrl}`
                                }
                                alt={`Screenshot of ${websiteData[0].domain}`}
                                className="w-full h-auto rounded-md shadow-sm"
                              />
                            </div>
                          )}
                          <p className="mb-4 text-gray-600">{websiteData[0].domain}</p>
                          <a
                            href={websiteData[0].domain.includes('://') ? websiteData[0].domain : `https://${websiteData[0].domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Visit Website
                          </a>
                        </div>
                      </div>
                    ) : company.hjemmeside ? (
                      <div className="p-6 flex flex-col items-center justify-center text-center h-full">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        <p className="mb-4 text-gray-600">{company.hjemmeside}</p>
                        <a
                          href={company.hjemmeside.includes('://') ? company.hjemmeside : `https://${company.hjemmeside}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Visit Website
                        </a>
                      </div>
                    ) : (
                      <div className="p-6 flex items-center justify-center text-center h-full">
                        <div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                          <p className="text-gray-600">{websiteError || 'No website found'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Company key info */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 h-full">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <h2 className="text-lg font-semibold text-gray-800">Key Information</h2>
                    </div>
                    <div className="p-6 space-y-4">
                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Industry</h3>
                        <p className="font-medium text-gray-800">{company.naeringskode1?.beskrivelse || '-'}</p>
                        {company.naeringskode1?.kode && (
                          <p className="text-sm text-gray-600">Code: {company.naeringskode1.kode}</p>
                        )}
                      </div>

                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Company Type</h3>
                        <p className="font-medium text-gray-800">{company.organisasjonsform?.beskrivelse || '-'}</p>
                      </div>

                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Employees</h3>
                        <p className="font-medium text-gray-800">
                          {company.antallAnsatte !== undefined ? company.antallAnsatte : '-'}
                        </p>
                      </div>

                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Founded</h3>
                        <p className="font-medium text-gray-800">{formatDate(company.stiftelsesdato)}</p>
                      </div>

                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Registration Date</h3>
                        <p className="font-medium text-gray-800">{formatDate(company.registreringsdatoEnhetsregisteret)}</p>
                      </div>

                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Latest Annual Report</h3>
                        <p className="font-medium text-gray-800">{company.sisteInnsendteAarsregnskap || '-'}</p>
                      </div>

                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Address</h3>
                        {company.forretningsadresse ? (
                          <div>
                            <p className="font-medium text-gray-800">
                              {company.forretningsadresse.adresse?.join(', ') || '-'}
                            </p>
                            <p className="text-gray-800">
                              {company.forretningsadresse.postnummer || ''} {company.forretningsadresse.poststed || ''}
                            </p>
                            <p className="text-gray-600">
                              {company.forretningsadresse.kommune || '-'}
                            </p>
                          </div>
                        ) : (
                          <p className="text-gray-600">No address registered</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial summary */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 h-full">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <h2 className="text-lg font-semibold text-gray-800">Financial Overview</h2>
                      {financialData.length > 0 && (
                        <p className="text-sm text-gray-500">
                          {new Date(financialData[0].regnskapsperiode.fraDato).getFullYear()}
                        </p>
                      )}
                    </div>

                    {financialLoading ? (
                      <div className="flex items-center justify-center p-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                      </div>
                    ) : financialData.length > 0 ? (
                      <div className="p-6 space-y-6">
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-sm text-gray-500 mb-1">Revenue</h3>
                            <p className="text-2xl font-bold text-gray-800">
                              {formatCurrency(financialData[0].resultatregnskapResultat?.driftsresultat?.driftsinntekter?.sumDriftsinntekter)}
                            </p>
                          </div>

                          <div>
                            <h3 className="text-sm text-gray-500 mb-1">Operating Result</h3>
                            <p className={`text-2xl font-bold ${
                              (financialData[0].resultatregnskapResultat?.driftsresultat?.driftsresultat < 0) 
                                ? 'text-red-600' 
                                : 'text-green-600'
                            }`}>
                              {formatCurrency(financialData[0].resultatregnskapResultat?.driftsresultat?.driftsresultat)}
                            </p>
                          </div>

                          <div>
                            <h3 className="text-sm text-gray-500 mb-1">Annual Result</h3>
                            <p className={`text-2xl font-bold ${
                              (financialData[0].resultatregnskapResultat?.aarsresultat < 0) 
                                ? 'text-red-600' 
                                : 'text-green-600'
                            }`}>
                              {formatCurrency(financialData[0].resultatregnskapResultat?.aarsresultat)}
                            </p>
                          </div>
                        </div>

                        <hr className="border-gray-200" />

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h3 className="text-sm text-gray-500 mb-1">Assets</h3>
                            <p className="font-medium text-gray-800">
                              {formatCurrency(financialData[0].eiendeler?.sumEiendeler)}
                            </p>
                          </div>

                          <div>
                            <h3 className="text-sm text-gray-500 mb-1">Equity</h3>
                            <p className={`font-medium ${
                              (financialData[0].egenkapitalGjeld?.egenkapital?.sumEgenkapital < 0) 
                                ? 'text-red-600' 
                                : 'text-gray-800'
                            }`}>
                              {formatCurrency(financialData[0].egenkapitalGjeld?.egenkapital?.sumEgenkapital)}
                            </p>
                          </div>

                          <div>
                            <h3 className="text-sm text-gray-500 mb-1">Debt</h3>
                            <p className="font-medium text-gray-800">
                              {formatCurrency(financialData[0].egenkapitalGjeld?.gjeldOversikt?.sumGjeld)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-6 flex items-center justify-center text-center h-full">
                        <div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-gray-600">{financialError || 'No financial data found'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Business purpose section */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mt-6">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-800">Business Purpose</h2>
                </div>

                {company.vedtektsfestetFormaal && company.vedtektsfestetFormaal.length > 0 ? (
                  <div className="p-6">
                    <p className="text-gray-800 italic border-l-4 pl-4 border-blue-200 bg-blue-50 p-3 rounded">
                      {company.vedtektsfestetFormaal.join(' ')}
                    </p>
                  </div>
                ) : (
                  <div className="p-6 flex items-center justify-center text-center h-full">
                    <div>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-600">No business purpose found</p>
                    </div>
                  </div>
                )}

                <div className="p-4 mt-2 border-t border-gray-200 bg-gray-50">
                  <a
                    href={`https://w2.brreg.no/enhet/sok/detalj.jsp?orgnr=${company.organisasjonsnummer}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline text-sm flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Brønnøysundregistrene
                  </a>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'info' && (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h2 className="text-lg font-semibold mb-2 text-gray-800">Company Information</h2>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">Company Type</p>
                    <p className="text-gray-800">{company.organisasjonsform?.beskrivelse || '-'}</p>
                  </div>

                  {/* Registration dates */}
                  <div className="border-t pt-2 mt-4">
                    <p className="text-sm font-semibold text-gray-700">Registration Dates</p>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Entity Register</p>
                      <p className="text-gray-800">{formatDate(company.registreringsdatoEnhetsregisteret)}</p>
                    </div>
                    {company.registreringsdatoForetaksregisteret && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Business Register</p>
                        <p className="text-gray-800">{formatDate(company.registreringsdatoForetaksregisteret)}</p>
                      </div>
                    )}
                    {company.stiftelsesdato && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Foundation Date</p>
                        <p className="text-gray-800">{formatDate(company.stiftelsesdato)}</p>
                      </div>
                    )}
                  </div>

                  {/* Registration status */}
                  <div className="border-t pt-2 mt-4">
                    <p className="text-sm font-semibold text-gray-700">Registration Status</p>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <p className="text-sm text-gray-500">Business Register</p>
                        <p className="text-gray-800">{company.registrertIForetaksregisteret ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">VAT Register</p>
                        <p className="text-gray-800">{company.registrertIMvaregisteret ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Company status */}
                  <div className="border-t pt-2 mt-4">
                    <p className="text-sm font-semibold text-gray-700">Status</p>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Employees</p>
                      <p className="text-gray-800">
                        {company.antallAnsatte !== undefined ?
                          company.antallAnsatte :
                          (company.harRegistrertAntallAnsatte === false ? '0-4' : '-')}
                      </p>
                    </div>
                    {company.sisteInnsendteAarsregnskap && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Latest Annual Report</p>
                        <p className="text-gray-800">{company.sisteInnsendteAarsregnskap}</p>
                      </div>
                    )}
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Bankruptcy</p>
                      <p className={company.konkurs ? 'text-red-600 font-medium' : 'text-gray-800'}>
                        {company.konkurs ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Under Liquidation</p>
                      <p className={company.underAvvikling ? 'text-orange-600 font-medium' : 'text-gray-800'}>
                        {company.underAvvikling ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>

                  {/* Website section showing either the discovered website or the registered one */}
                  <div className="border-t pt-2 mt-4">
                    <p className="text-sm text-gray-500">Website</p>
                    {websiteData && websiteData.length > 0 ? (
                      <a
                        href={websiteData[0].domain.includes('://') ? websiteData[0].domain : `https://${websiteData[0].domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        {websiteData[0].domain}
                      </a>
                    ) : company.hjemmeside ? (
                      <a
                        href={company.hjemmeside.includes('://') ? company.hjemmeside : `https://${company.hjemmeside}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        {company.hjemmeside}
                      </a>
                    ) : (
                      <p className="text-gray-600">No website found</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold mb-2 text-gray-800">Address</h2>
                {company.forretningsadresse ? (
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-gray-500">Street Address</p>
                      <p className="text-gray-800">{company.forretningsadresse.adresse?.join(', ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Postal Code & City</p>
                      <p className="text-gray-800">
                        {company.forretningsadresse.postnummer || '-'} {company.forretningsadresse.poststed || ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Municipality</p>
                      <p className="text-gray-800">
                        {company.forretningsadresse.kommune
                          ? `${company.forretningsadresse.kommune} (${company.forretningsadresse.kommunenummer})`
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Country</p>
                      <p className="text-gray-800">{company.forretningsadresse.land || 'Norway'}</p>
                    </div>
                  </div>
                ) : (
                  <p>No address information available</p>
                )}

                {/* Industry codes section */}
                <div className="border-t pt-4 mt-6">
                  <h2 className="text-lg font-semibold mb-2 text-gray-800">Industry Codes</h2>
                  {company.naeringskode1 ? (
                    <div className="bg-gray-50 p-3 rounded border">
                      <p className="font-medium text-gray-800">{company.naeringskode1.kode}</p>
                      <p className="text-gray-700">{company.naeringskode1.beskrivelse}</p>
                    </div>
                  ) : (
                    <p>No industry codes registered</p>
                  )}
                </div>

                {/* Website preview section */}
                {websiteData && websiteData.length > 0 && websiteData[0].screenshotUrl && (
                  <div className="border-t pt-4 mt-6">
                    <h2 className="text-lg font-semibold mb-2 text-gray-800">Website Preview</h2>
                    <div className="bg-gray-50 p-2 rounded border">
                      <img
                        src={
                          websiteData[0].screenshotUrl.startsWith('http')
                            ? websiteData[0].screenshotUrl
                            : websiteData[0].screenshotUrl.startsWith('/uploads')
                              ? websiteData[0].screenshotUrl
                              : `/uploads${websiteData[0].screenshotUrl}`
                        }
                        alt={`Screenshot of ${websiteData[0].domain}`}
                        className="w-full h-auto rounded shadow-sm"
                      />
                      <div className="mt-2 text-center">
                        <a
                          href={websiteData[0].domain.includes('://') ? websiteData[0].domain : `https://${websiteData[0].domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline text-sm inline-flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Visit {websiteData[0].domain}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Business purpose section */}
                {company.vedtektsfestetFormaal && (
                  <div className="border-t pt-4 mt-6">
                    <h2 className="text-lg font-semibold mb-2 text-gray-800">Business Purpose</h2>
                    <div className="mb-4">
                      <p className="text-sm text-gray-500 mb-1">Charter Purpose</p>
                      <p className="bg-gray-50 p-3 rounded border text-gray-800">
                        {company.vedtektsfestetFormaal.join(' ')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Sector code */}
                {company.institusjonellSektorkode && (
                  <div className="border-t pt-4 mt-6">
                    <h2 className="text-lg font-semibold mb-2 text-gray-800">Sector Code</h2>
                    <div className="bg-gray-50 p-3 rounded border">
                      <p className="font-medium text-gray-800">{company.institusjonellSektorkode.kode}</p>
                      <p className="text-gray-700">{company.institusjonellSektorkode.beskrivelse}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          

          {activeTab === 'people' && <PeopleTab company={company} websiteData={websiteData} />}

          {activeTab === 'financials' && (
            <div className="p-6">
              {financialLoading ? (
                <div className="flex items-center justify-center p-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : financialData.length > 0 ? (
                <div>
                  <h2 className="text-xl font-semibold mb-6 text-gray-800">Financial History</h2>
                  
                  {/* Financial overview for most recent year */}
                  <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-8">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {new Date(financialData[0].regnskapsperiode.fraDato).getFullYear()} Overview
                      </h3>
                    </div>
                    
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <h4 className="text-sm text-gray-500 mb-1">Revenue</h4>
                          <p className="text-2xl font-bold text-gray-800">
                            {formatCurrency(financialData[0].resultatregnskapResultat?.driftsresultat?.driftsinntekter?.sumDriftsinntekter)}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm text-gray-500 mb-1">Operating Result</h4>
                          <p className={`text-2xl font-bold ${
                            (financialData[0].resultatregnskapResultat?.driftsresultat?.driftsresultat < 0) 
                              ? 'text-red-600' 
                              : 'text-green-600'
                          }`}>
                            {formatCurrency(financialData[0].resultatregnskapResultat?.driftsresultat?.driftsresultat)}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm text-gray-500 mb-1">Annual Result</h4>
                          <p className={`text-2xl font-bold ${
                            (financialData[0].resultatregnskapResultat?.aarsresultat < 0) 
                              ? 'text-red-600' 
                              : 'text-green-600'
                          }`}>
                            {formatCurrency(financialData[0].resultatregnskapResultat?.aarsresultat)}
                          </p>
                        </div>
                      </div>
                      
                      <hr className="my-6 border-gray-200" />
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <h4 className="text-sm text-gray-500 mb-1">Assets</h4>
                          <p className="text-lg font-semibold text-gray-800">
                            {formatCurrency(financialData[0].eiendeler?.sumEiendeler)}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm text-gray-500 mb-1">Equity</h4>
                          <p className={`text-lg font-semibold ${
                            (financialData[0].egenkapitalGjeld?.egenkapital?.sumEgenkapital < 0) 
                              ? 'text-red-600' 
                              : 'text-gray-800'
                          }`}>
                            {formatCurrency(financialData[0].egenkapitalGjeld?.egenkapital?.sumEgenkapital)}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm text-gray-500 mb-1">Debt</h4>
                          <p className="text-lg font-semibold text-gray-800">
                            {formatCurrency(financialData[0].egenkapitalGjeld?.gjeldOversikt?.sumGjeld)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Financial data table for all available years */}
                  <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <h3 className="text-lg font-semibold text-gray-800">Financial History</h3>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Year
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Revenue
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Operating Result
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Annual Result
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Total Assets
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Equity
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {financialData.map((yearData, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {new Date(yearData.regnskapsperiode.fraDato).getFullYear()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(yearData.resultatregnskapResultat?.driftsresultat?.driftsinntekter?.sumDriftsinntekter)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <span className={yearData.resultatregnskapResultat?.driftsresultat?.driftsresultat < 0 ? 'text-red-600' : 'text-green-600'}>
                                  {formatCurrency(yearData.resultatregnskapResultat?.driftsresultat?.driftsresultat)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <span className={yearData.resultatregnskapResultat?.aarsresultat < 0 ? 'text-red-600' : 'text-green-600'}>
                                  {formatCurrency(yearData.resultatregnskapResultat?.aarsresultat)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(yearData.eiendeler?.sumEiendeler)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <span className={yearData.egenkapitalGjeld?.egenkapital?.sumEgenkapital < 0 ? 'text-red-600' : ''}>
                                  {formatCurrency(yearData.egenkapitalGjeld?.egenkapital?.sumEgenkapital)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 flex items-center justify-center text-center">
                  <div>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-xl font-medium text-gray-800 mb-2">No Financial Data</h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      {financialError || 'There is no financial data available for this company.'}
                    </p>
                    <div className="mt-6">
                      <a
                        href={`https://w2.brreg.no/enhet/sok/detalj.jsp?orgnr=${company.organisasjonsnummer}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Check Brønnøysundregistrene
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProspectorDetail;